// Shared fetch client (ARCHITECTURE §4, §8). Every API call in the web app goes through `api`.
// - cookies: `credentials: 'include'` (httpOnly `st_session`)
// - CSRF double-submit: unsafe methods send header `x-csrf-token` = cookie `st_csrf`
//   (fetched from GET /api/auth/csrf when the cookie is missing; retried once on a `csrf` error)
// - errors: `{ error: { code, message, details? } }` → thrown as `ApiError`
// - 401 unauthenticated → /sign-in?next=…   401 mfa_required → /sign-in/totp   402 read_only → read-only banner
import type { ErrorCode } from '@seventide/types';
import { markReadOnly } from './readOnly';

export type ApiErrorCode = ErrorCode | 'network_error' | 'bad_response' | (string & {});

export class ApiError extends Error {
  readonly status: number;
  readonly code: ApiErrorCode;
  readonly details: unknown;

  constructor(status: number, code: ApiErrorCode, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }

  /** Field errors from a 400 validation_error, when the server sends `details.fieldErrors` / `details.issues`. */
  get fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    const d = this.details as
      | { fieldErrors?: Record<string, string[] | string>; issues?: { path?: (string | number)[]; message?: string }[] }
      | undefined;
    if (d?.fieldErrors) {
      for (const [k, v] of Object.entries(d.fieldErrors)) {
        const msg = Array.isArray(v) ? v[0] : v;
        if (msg) out[k] = msg;
      }
    }
    if (Array.isArray(d?.issues)) {
      for (const i of d.issues) {
        const key = (i.path ?? []).join('.');
        if (key && i.message && !out[key]) out[key] = i.message;
      }
    }
    return out;
  }
}

export function isApiError(e: unknown, code?: ApiErrorCode): e is ApiError {
  return e instanceof ApiError && (code === undefined || e.code === code);
}

/** Human message for any thrown value — use in error alerts/toasts. */
export function errorMessage(e: unknown, fallback = 'Something went wrong. Please try again.'): string {
  if (e instanceof ApiError) return e.message || fallback;
  if (e instanceof Error && e.message) return e.message;
  return fallback;
}

type QueryValue = string | number | boolean | null | undefined;

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /** JSON body (objects are JSON-encoded) or FormData (sent as multipart). */
  body?: unknown;
  /** Appended as a query string; null/undefined values are skipped. */
  query?: Record<string, QueryValue | QueryValue[]>;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /**
   * When true (default) a 401 `unauthenticated` redirects to /sign-in?next=<current path>.
   * Set false for session probing (e.g. GET /api/me in useMe) and on the auth pages.
   */
  redirectOn401?: boolean;
}

// ---------------------------------------------------------------- navigation hook (set by the router)

type Navigate = (to: string, opts?: { replace?: boolean }) => void;
let navigateImpl: Navigate = (to, opts) => {
  if (opts?.replace) window.location.replace(to);
  else window.location.assign(to);
};
let onUnauthenticated: (() => void) | null = null;

/** The router registers its `navigate` so redirects stay client-side (see app/App.tsx). */
export function setApiNavigator(fn: Navigate): void {
  navigateImpl = fn;
}

/** Called after a 401 `unauthenticated` (App clears the query cache). */
export function setOnUnauthenticated(fn: (() => void) | null): void {
  onUnauthenticated = fn;
}

const AUTH_PATHS = ['/sign-in', '/auth/verify'];

function isOnAuthPage(): boolean {
  const p = window.location.pathname;
  return AUTH_PATHS.some((a) => p === a || p.startsWith(`${a}/`));
}

export function currentPathForNext(): string {
  return `${window.location.pathname}${window.location.search}`;
}

/** Sanitise a `next` / `redirectTo` value: only same-origin absolute paths. */
export function safeNextPath(next: string | null | undefined, fallback = '/'): string {
  if (!next || !next.startsWith('/') || next.startsWith('//') || next.startsWith('/\\')) return fallback;
  if (AUTH_PATHS.some((a) => next === a || next.startsWith(`${a}?`) || next.startsWith(`${a}/`))) return fallback;
  return next;
}

// ---------------------------------------------------------------- CSRF

const CSRF_COOKIE = 'st_csrf';
let csrfMemory: string | null = null;
let csrfInflight: Promise<string> | null = null;

export function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const parts = document.cookie ? document.cookie.split('; ') : [];
  for (const part of parts) {
    const idx = part.indexOf('=');
    const k = idx === -1 ? part : part.slice(0, idx);
    if (k === name) return decodeURIComponent(part.slice(idx + 1));
  }
  return null;
}

/** Remember a token received from /api/me (`csrfToken`) as a fallback when cookies can't be read. */
export function rememberCsrfToken(token: string | null | undefined): void {
  if (token) csrfMemory = token;
}

async function fetchCsrfToken(): Promise<string> {
  if (!csrfInflight) {
    csrfInflight = (async () => {
      const res = await rawFetch('/api/auth/csrf', { method: 'GET' });
      const data = (await parseJson(res)) as { csrfToken?: string } | undefined;
      if (!res.ok || !data?.csrfToken) throw toApiError(res, data);
      csrfMemory = data.csrfToken;
      return data.csrfToken;
    })().finally(() => {
      csrfInflight = null;
    });
  }
  return csrfInflight;
}

async function getCsrfToken(forceRefresh = false): Promise<string> {
  if (!forceRefresh) {
    const fromCookie = readCookie(CSRF_COOKIE);
    if (fromCookie) return fromCookie;
    if (csrfMemory) return csrfMemory;
  }
  return fetchCsrfToken();
}

/** Test helper: forget cached CSRF state. */
export function resetCsrfForTests(): void {
  csrfMemory = null;
  csrfInflight = null;
}

// ---------------------------------------------------------------- core

function buildUrl(path: string, query?: RequestOptions['query']): string {
  if (!query) return path;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    const values = Array.isArray(v) ? v : [v];
    for (const item of values) {
      if (item === null || item === undefined || item === '') continue;
      params.append(k, String(item));
    }
  }
  const qs = params.toString();
  if (!qs) return path;
  return `${path}${path.includes('?') ? '&' : '?'}${qs}`;
}

async function rawFetch(url: string, init: RequestInit): Promise<Response> {
  try {
    return await fetch(url, { credentials: 'include', ...init });
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') throw e;
    throw new ApiError(
      0,
      'network_error',
      "We couldn't reach Seventide. Check your internet connection and try again.",
    );
  }
}

async function parseJson(res: Response): Promise<unknown> {
  if (res.status === 204) return undefined;
  const text = await res.text();
  if (!text) return undefined;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return undefined;
  }
}

const DEFAULT_MESSAGES: Record<number, string> = {
  400: 'Some of the details need checking.',
  401: 'Please sign in to continue.',
  402: 'This workspace is read-only until the subscription is renewed.',
  403: "You don't have permission to do that.",
  404: "We couldn't find that. It may have been removed, or you may not have access.",
  409: 'This was changed by someone else. Refresh and try again.',
  429: 'Too many attempts. Please wait a minute and try again.',
};

function toApiError(res: Response, data: unknown): ApiError {
  const body = data as { error?: { code?: string; message?: string; details?: unknown } } | undefined;
  const code = body?.error?.code ?? (res.status >= 500 ? 'internal' : 'bad_response');
  const message =
    body?.error?.message ??
    DEFAULT_MESSAGES[res.status] ??
    'Something went wrong on our side. Please try again in a moment.';
  return new ApiError(res.status, code, message, body?.error?.details);
}

const UNSAFE = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

async function request<T>(path: string, opts: RequestOptions = {}, isRetry = false): Promise<T> {
  const method = opts.method ?? 'GET';
  const headers: Record<string, string> = { Accept: 'application/json', ...opts.headers };
  let body: BodyInit | undefined;
  if (opts.body instanceof FormData) {
    body = opts.body; // browser sets the multipart boundary
  } else if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(opts.body);
  }
  if (UNSAFE.has(method)) headers['x-csrf-token'] = await getCsrfToken(isRetry);

  const res = await rawFetch(buildUrl(path, opts.query), { method, headers, body, signal: opts.signal });
  const data = await parseJson(res);
  if (res.ok) return data as T;

  const err = toApiError(res, data);

  // Stale CSRF token (e.g. after sign-in rotated it): refresh once and retry.
  if (err.code === 'csrf' && !isRetry && UNSAFE.has(method)) {
    return request<T>(path, opts, true);
  }

  handleGlobalError(err, opts);
  throw err;
}

function handleGlobalError(err: ApiError, opts: RequestOptions): void {
  if (err.status === 401 && err.code === 'mfa_required') {
    if (window.location.pathname !== '/sign-in/totp') {
      navigateImpl(`/sign-in/totp?next=${encodeURIComponent(safeNextPath(currentPathForNext()))}`);
    }
    return;
  }
  if (err.status === 401 && err.code === 'unauthenticated') {
    onUnauthenticated?.();
    if (opts.redirectOn401 !== false && !isOnAuthPage()) {
      navigateImpl(`/sign-in?next=${encodeURIComponent(safeNextPath(currentPathForNext()))}`, { replace: true });
    }
    return;
  }
  if (err.status === 402 && err.code === 'read_only') {
    markReadOnly(err.message);
  }
}

type BodyOpts = Omit<RequestOptions, 'method' | 'body'>;

export const api = {
  request,
  get: <T>(path: string, opts?: Omit<RequestOptions, 'method' | 'body'>) => request<T>(path, { ...opts, method: 'GET' }),
  post: <T>(path: string, body?: unknown, opts?: BodyOpts) => request<T>(path, { ...opts, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, opts?: BodyOpts) => request<T>(path, { ...opts, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, opts?: BodyOpts) => request<T>(path, { ...opts, method: 'PATCH', body }),
  delete: <T>(path: string, opts?: BodyOpts) => request<T>(path, { ...opts, method: 'DELETE' }),
  /**
   * Multipart upload. `fields` are appended as text parts; `file` (or several) under `fileField` (default "file").
   * Example: `api.upload<DocumentDto>(`/api/w/${wsId}/documents`, file, { category: 'will' })`.
   */
  upload: <T>(
    path: string,
    file: Blob | Blob[],
    fields: Record<string, string | number | boolean | null | undefined> = {},
    opts: BodyOpts & { fileField?: string; fileName?: string } = {},
  ) => {
    const form = new FormData();
    for (const [k, v] of Object.entries(fields)) {
      if (v !== null && v !== undefined) form.append(k, String(v));
    }
    const files = Array.isArray(file) ? file : [file];
    const field = opts.fileField ?? 'file';
    for (const f of files) {
      const name = f instanceof File ? f.name : (opts.fileName ?? 'upload');
      form.append(field, f, name);
    }
    return request<T>(path, {
      query: opts.query,
      headers: opts.headers,
      signal: opts.signal,
      redirectOn401: opts.redirectOn401,
      method: 'POST',
      body: form,
    });
  },
};

/** Build a workspace-scoped API path: `wsPath(wsId, 'gifts', giftId)` → `/api/w/<wsId>/gifts/<giftId>`. */
export function wsPath(wsId: string, ...segments: (string | number)[]): string {
  const rest = segments.map((s) => encodeURIComponent(String(s))).join('/');
  return `/api/w/${encodeURIComponent(wsId)}${rest ? `/${rest}` : ''}`;
}
